import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { BookOpen, X, Search, CreditCard, Clock, User, CheckSquare } from 'lucide-react'

/**
 * Multi-course enrollment picker for students (first login or add courses).
 */
export default function CoursePickerModal({
  onClose,
  onEnrolled,
  title = 'Select Your Courses',
  subtitle = 'Choose one or more courses. An admin will activate your enrollment after review.',
  dismissible = true
}) {
  const [availableCourses, setAvailableCourses] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    api.get('/students/available-courses')
      .then(({ data }) => setAvailableCourses(data))
      .catch(() => toast.error('Failed to load courses'))
      .finally(() => setLoading(false))
  }, [])

  const toggleCourse = (id) => {
    const sid = String(id)
    setSelectedIds(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    )
  }

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one course')
      return
    }
    setSubmitting(true)
    try {
      const { data } = await api.post('/students/enroll', {
        course_ids: selectedIds.map(Number)
      })
      toast.success(data.message)
      onEnrolled?.()
      onClose()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit enrollment')
    } finally {
      setSubmitting(false)
    }
  }

  const filtered = availableCourses.filter(c =>
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    (c.category || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.course_code || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          </div>
          {dismissible && (
            <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <X size={22} />
            </button>
          )}
        </div>

        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search courses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 dark:bg-gray-900"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading courses...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">
                {availableCourses.length === 0
                  ? 'No courses available to enroll right now.'
                  : 'No courses match your search.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(course => {
                const checked = selectedIds.includes(String(course.id))
                return (
                  <label
                    key={course.id}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      checked
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-primary-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 accent-primary-500"
                      checked={checked}
                      onChange={() => toggleCourse(course.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 dark:text-white">{course.title}</span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{course.course_code}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                        {course.trainer_name && (
                          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{course.trainer_name}</span>
                        )}
                        {course.duration_weeks && (
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{course.duration_weeks} weeks</span>
                        )}
                        {course.fee && (
                          <span className="flex items-center gap-1 text-primary-600 font-medium">
                            <CreditCard className="w-3.5 h-3.5" />
                            {Number(course.fee).toLocaleString()} UGX
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 dark:bg-gray-900/50 rounded-b-xl flex flex-col sm:flex-row gap-3 items-center justify-between">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <CheckSquare size={14} />
            {selectedIds.length} course{selectedIds.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-2 w-full sm:w-auto">
            {dismissible && (
              <button type="button" onClick={onClose} className="btn btn-outline flex-1 sm:flex-none">
                {selectedIds.length === 0 ? 'Skip for now' : 'Cancel'}
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || selectedIds.length === 0}
              className="btn btn-primary flex-1 sm:flex-none disabled:opacity-60"
            >
              {submitting ? 'Submitting...' : `Request Enrollment${selectedIds.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
